import React from 'react';
import Button from '@atlaskit/button';

import { ModalHeader, ModalBody, ModalFooter, ModalTitle } from '@atlaskit/modal-dialog';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	MODAL,
	ContextualAnalyticsData,
} from '@atlassian/jira-product-analytics-bridge';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { DeleteCardType } from '../../../model/modals/types.tsx';
import type { Card } from '../../../model/types.tsx';
import { cardDeleteRequest } from '../../../state/actions/cards/delete/index.tsx';
import { closeModal } from '../../../state/actions/modals/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../state/index.tsx';
import { getCards } from '../../../state/selectors/cards/index.tsx';
import { getOpenModalData } from '../../../state/selectors/modals/index.tsx';
import messages from './messages.tsx';

function CardDeleteModal() {
	const card: Card | null = useBacklogSelector((state) => {
		const modalData = getOpenModalData(state);

		if (!modalData || modalData.type !== DeleteCardType) {
			return null;
		}

		const {
			payload: { cardId },
		} = modalData;
		return cardId ? getCards(state)[String(cardId)] : null;
	});

	const dispatch = useBacklogDispatch();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/*
      Should only need to create this once since the modal is closed
      by either confirm or cancel
    */
	const analyticsEvent = createAnalyticsEvent({
		actionSubject: 'button',
		action: 'clicked',
	});

	const onDelete = () => {
		fireUIAnalytics(analyticsEvent, 'confirmButton');
		card && dispatch(cardDeleteRequest({ cardId: card.id }));
		dispatch(closeModal());
	};

	const onClose = () => {
		fireUIAnalytics(analyticsEvent, 'cancelButton');
		dispatch(closeModal());
	};

	const { formatMessage } = useIntl();

	if (!card) {
		return null;
	}

	return (
		<ShortcutScope>
			<ModalDialog width={400} onClose={onClose}>
				<ModalHeader>
					<ModalTitle appearance="danger">
						<FormattedMessage {...messages.title} values={{ cardKey: card.key }} />
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p>
						{formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.deleteDataPermanentlyWarningIssueTermRefresh
								: messages.deleteDataPermanentlyWarning,
						)}
					</p>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p>
						{formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.resolveOrCloseInsteadIssueTermRefresh
								: messages.resolveOrCloseInstead,
						)}
					</p>
					<ViewTracker />
				</ModalBody>
				<ModalFooter>
					{isVisualRefreshEnabled() ? (
						<>
							<Button appearance="subtle" onClick={onClose}>
								{formatMessage(messages.cancel)}
							</Button>
							<Button appearance="danger" onClick={onDelete}>
								{formatMessage(messages.delete)}
							</Button>
						</>
					) : (
						<>
							<Button appearance="danger" onClick={onDelete}>
								{formatMessage(messages.delete)}
							</Button>
							<Button appearance="subtle" onClick={onClose}>
								{formatMessage(messages.cancel)}
							</Button>
						</>
					)}
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
}

export default function CardDeleteModalWithAnalytics() {
	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="deleteCard">
			<CardDeleteModal />
		</ContextualAnalyticsData>
	);
}
